(function () {
  const AB_CLASS = 'ab-search-input';
  const CORE_SECTION = 'predictive-search';
  const AB_SECTION = 'predictive-search-ab';
  const DESKTOP_MQ = window.matchMedia('(min-width: 750px)');

  function isAbActive() {
    return document.documentElement.classList.contains(AB_CLASS);
  }

  function patchPredictiveSearchFetch() {
    if (window.__abSearchInputFetchPatched) return;

    window.__abSearchInputFetchPatched = true;
    const originalFetch = window.fetch;

    window.fetch = function (input, init) {
      let url = typeof input === 'string' ? input : input?.url;
      const useAbSection = isAbActive() && url?.includes(`section_id=${CORE_SECTION}`);

      if (useAbSection) {
        url = url.replace(`section_id=${CORE_SECTION}`, `section_id=${AB_SECTION}`);
        input = typeof input === 'string' ? url : new Request(url, input);
      }

      return originalFetch.call(this, input, init).then((response) => {
        if (!useAbSection) {
          return response;
        }

        return response.text().then((text) => {
          const patchedText = text
            .replaceAll(`shopify-section-${AB_SECTION}`, `shopify-section-${CORE_SECTION}`)
            .replaceAll(`id="${AB_SECTION}"`, `id="${CORE_SECTION}"`);

          return new Response(patchedText, {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          });
        });
      });
    };
  }

  function getHeaderOverlayMetrics() {
    const wrapper = document.querySelector('[data-header-wrapper]');
    const sections = document.querySelector('.header-sections');

    if (!wrapper) {
      return { top: 0, height: window.innerHeight };
    }

    const top = wrapper.classList.contains('js__header__stuck')
      ? wrapper.getBoundingClientRect().bottom
      : (sections || wrapper).getBoundingClientRect().bottom;

    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

    return {
      top,
      height: Math.max(0, viewportHeight - top),
    };
  }

  function updateHeaderOverlayMetrics() {
    if (!isAbActive() || !DESKTOP_MQ.matches) {
      document.documentElement.style.removeProperty('--ab-search-overlay-top');
      document.documentElement.style.removeProperty('--ab-search-overlay-height');
      return;
    }

    const { top, height } = getHeaderOverlayMetrics();

    document.documentElement.style.setProperty('--ab-search-overlay-top', `${top}px`);
    document.documentElement.style.setProperty('--ab-search-overlay-height', `${height}px`);
  }

  function isHeaderInteracting() {
    return Boolean(
      document.querySelector(
        '.header__menu--v2 .is-visible, .header__menu--v2 .header__dropdown.is-visible, .header-search-inline--desktop predictive-search[open]'
      )
    );
  }

  function getAbHeaderHeight() {
    const header = document.querySelector('[data-header-height]');
    if (!header) return 0;

    const headerTop = header.getBoundingClientRect().top;
    let maxBottom = header.getBoundingClientRect().bottom;

    header.querySelectorAll(
      '.header__desktop, .header__mobile, .toolbar, .header__search-inline, .header__menu--v2'
    ).forEach((el) => {
      const { bottom } = el.getBoundingClientRect();
      if (bottom > maxBottom) maxBottom = bottom;
    });

    return Math.ceil(maxBottom - headerTop);
  }

  let heightUpdateTimer;

  function updateHeaderHeight() {
    const header = document.querySelector('[data-header-height]');
    if (!header) return;

    if (!isAbActive()) {
      document.documentElement.style.removeProperty('--header-height');
      document.dispatchEvent(new CustomEvent('theme:resize'));
      return;
    }

    if (isHeaderInteracting()) return;

    const height = getAbHeaderHeight();
    if (!height) return;

    const current = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
      10
    );

    if (Math.abs(current - height) < 2) return;

    document.documentElement.style.setProperty('--header-height', `${height}px`);
    document.dispatchEvent(new CustomEvent('theme:resize'));
  }

  function scheduleHeaderHeightUpdate() {
    clearTimeout(heightUpdateTimer);
    heightUpdateTimer = setTimeout(updateHeaderHeight, 200);
  }

  function bindHeaderHeightSync() {
    if (window.__abSearchInputHeightBound) return;

    window.__abSearchInputHeightBound = true;

    const scheduleUpdate = () => {
      requestAnimationFrame(scheduleHeaderHeightUpdate);
    };

    const observeHeader = () => {
      if (!('ResizeObserver' in window)) return;

      const header = document.querySelector('[data-header-height]');
      if (!header || header.dataset.abHeightObserved) return;

      header.dataset.abHeightObserved = 'true';
      new ResizeObserver(scheduleUpdate).observe(header);
    };

    const init = () => {
      scheduleUpdate();
      observeHeader();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    new MutationObserver(scheduleUpdate).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('load', scheduleUpdate);
    document.addEventListener('shopify:section:load', init);
  }

  function updateMegaMenuPosition() {
    if (!isAbActive() || !DESKTOP_MQ.matches) {
      document.documentElement.style.removeProperty('--ab-mega-menu-top');
      return;
    }

    const menu = document.querySelector('.header__menu--v2');
    if (!menu) return;

    document.documentElement.style.setProperty(
      '--ab-mega-menu-top',
      `${Math.ceil(menu.getBoundingClientRect().bottom)}px`
    );
  }

  function patchV2MenuHover() {
    const menu = document.querySelector('.header__menu--v2');
    if (!menu || menu.dataset.abHoverPatched) return;

    menu.dataset.abHoverPatched = 'true';

    const wrapper = document.querySelector('[data-header-wrapper]');
    let hideTimer;

    const clearHideTimer = () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
    };

    const hideItem = (trigger) => {
      const disclosure = trigger.querySelector('[data-hover-disclosure]');
      trigger.classList.remove('is-visible');
      disclosure?.classList.remove('is-visible');
      trigger.setAttribute('aria-expanded', 'false');

      if (!menu.querySelector('.menu__item.is-visible')) {
        wrapper?.classList.remove('meganav--visible', 'meganav--is-transitioning');
      }
    };

    menu.addEventListener('mouseenter', () => {
      clearHideTimer();
      updateMegaMenuPosition();
    }, true);

    menu.querySelectorAll('[data-hover-disclosure-toggle]').forEach((trigger) => {
      trigger.addEventListener(
        'mouseenter',
        () => {
          clearHideTimer();
          updateMegaMenuPosition();
          menu.querySelectorAll('[data-hover-disclosure-toggle].is-visible').forEach((other) => {
            if (other !== trigger) hideItem(other);
          });
        },
        true
      );

      trigger.addEventListener(
        'mouseleave',
        (event) => {
          if (trigger.contains(event.relatedTarget)) return;

          const disclosure = trigger.querySelector('[data-hover-disclosure]');
          if (disclosure?.contains(event.relatedTarget)) return;

          event.stopImmediatePropagation();
          clearHideTimer();

          hideTimer = setTimeout(() => {
            if (!trigger.matches(':hover') && !disclosure?.matches(':hover')) {
              hideItem(trigger);
            }
          }, 200);
        },
        true
      );
    });
  }

  function bindDesktopInlineSearchOverlay() {
    const inlineSearch = document.querySelector('.header-search-inline--desktop predictive-search');
    const headerSections = document.querySelector('.header-sections');
    const headerWrapper = document.querySelector('[data-header-wrapper]');

    if (!inlineSearch || inlineSearch.dataset.abOverlayBound) return;

    inlineSearch.dataset.abOverlayBound = 'true';

    const scheduleUpdate = () => {
      requestAnimationFrame(updateHeaderOverlayMetrics);
    };

    const onOpenChange = () => {
      if (!isAbActive() || !DESKTOP_MQ.matches) return;

      const isOpen = inlineSearch.hasAttribute('open');

      document.dispatchEvent(
        new CustomEvent(isOpen ? 'theme:scroll:lock' : 'theme:scroll:unlock', { bubbles: true })
      );

      if (isOpen) {
        scheduleUpdate();
      }
    };

    new MutationObserver(onOpenChange).observe(inlineSearch, {
      attributes: true,
      attributeFilter: ['open'],
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        if (inlineSearch.hasAttribute('open')) scheduleUpdate();
      });

      if (headerSections) resizeObserver.observe(headerSections);
      if (headerWrapper) resizeObserver.observe(headerWrapper);
    }

    if (headerWrapper) {
      new MutationObserver(() => {
        if (inlineSearch.hasAttribute('open')) scheduleUpdate();
      }).observe(headerWrapper, { attributes: true, attributeFilter: ['class'] });
    }

    window.addEventListener('resize', scheduleUpdate);
    document.addEventListener('theme:resize', scheduleUpdate);
    window.addEventListener(
      'scroll',
      () => {
        if (inlineSearch.hasAttribute('open')) scheduleUpdate();
      },
      { passive: true }
    );

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleUpdate);
    }

    DESKTOP_MQ.addEventListener('change', scheduleUpdate);
  }

  function bindMegaMenuPositionSync() {
    if (window.__abMegaMenuPositionBound) return;

    window.__abMegaMenuPositionBound = true;

    const scheduleUpdate = () => {
      requestAnimationFrame(updateMegaMenuPosition);
    };

    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    document.addEventListener('theme:resize', scheduleUpdate);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleUpdate);
    }

    DESKTOP_MQ.addEventListener('change', scheduleUpdate);
  }

  patchPredictiveSearchFetch();
  bindHeaderHeightSync();
  bindMegaMenuPositionSync();
  bindDesktopInlineSearchOverlay();

  function initAbSearchInput() {
    patchV2MenuHover();
    bindDesktopInlineSearchOverlay();
    updateMegaMenuPosition();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAbSearchInput);
  } else {
    initAbSearchInput();
  }

  document.addEventListener('shopify:section:load', initAbSearchInput);
})();
